import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FaWallet } from 'react-icons/fa';
import { PiArrowsSplitBold, PiInfoBold } from 'react-icons/pi';
import { Button } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';
import toast from 'react-hot-toast';
import type { WalletAppliedItem } from './types';
import WalletReallocateModal from './WalletReallocateModal';
import { isSuperAdmin } from '@v2/lib/auth';

type AllocatableDeal = {
    ploomes_deal_id: number;
    title?: string | null;
    sale_number?: string | null;
};

type WalletSnapshot = {
    balance: number;
    wallet_applied_total?: number;
    entries?: Array<{
        id: number;
        type: string;
        amount: number;
        note?: string | null;
        deal_number?: string | null;
        commission_installment_id?: number | null;
    }>;
    allocatable_deals?: AllocatableDeal[];
};

type ApprovalAction = 'uncancel' | 'wallet_manual' | 'wallet_allocate' | 'wallet_forgive' | 'wallet_sync' | 'cancel';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

export function useCommissionActionPermissions() {
    const me = useQuery({
        queryKey: ['me'],
        queryFn: () => api.get('/me').then((r) => r.data as { permissions?: string[]; roles?: string[] }),
        staleTime: 60_000,
    });
    const permissions: string[] = me.data?.permissions ?? [];
    const roles: string[] = me.data?.roles ?? [];
    const isSuper = roles.includes('super_admin');

    return {
        canCancel: isSuper || permissions.includes('CancelCommission:CommissionInstallment'),
        // Reabilitar comissão perdida: exceção aprovada por Mario/Bruno, executada só por super_admin.
        canUncancel: isSuper,
        canWallet: isSuper || permissions.includes('ManageWallet:CommissionInstallment'),
        // RH realoca estorno que excede a comissão do mês.
        canMoveWallet: isSuper || roles.includes('hr') || permissions.includes('ManageWallet:CommissionInstallment'),
        canCancelSale: ['super_admin', 'hr', 'financial_director', 'commercial_director'].some((r) => roles.includes(r)),
    };
}

export function CommissionApprovalModal({
    open,
    action,
    title,
    installmentId,
    sellerUserId,
    onClose,
    onConfirmed,
}: {
    open: boolean;
    action: ApprovalAction;
    title?: string;
    /** Contexto do e-mail do aprovador (o servidor monta venda/parcela ou vendedor). */
    installmentId?: number | null;
    sellerUserId?: number | null;
    onClose: () => void;
    onConfirmed: (code: string, reason: string) => Promise<void>;
}) {
    const { t } = useI18n();
    const [code, setCode] = useState('');
    const [reason, setReason] = useState('');
    const [sent, setSent] = useState(false);
    const [approverId, setApproverId] = useState<number | null>(null);

    const approvers = useQuery({
        queryKey: ['commissioning', 'director-approval-approvers', action],
        queryFn: () => api.get('/commissioning/director-approval/approvers', { params: { action } })
            .then((r) => r.data.data as Array<{ id: number; name: string; email?: string | null }>),
        enabled: open,
        staleTime: 60_000,
    });
    const approverList = approvers.data ?? [];
    const ready = sent && code.length === 6 && reason.trim().length >= 3;

    const requestCode = useMutation({
        mutationFn: () => api.post('/commissioning/director-approval/request', {
            action,
            approver_user_id: approverId,
            commission_installment_id: installmentId ?? null,
            seller_user_id: sellerUserId ?? null,
        }),
        onSuccess: (res) => {
            setSent(true);
            toast.success(res.data?.message ?? t('v2.commissioning.director_approval.sent'));
        },
        onError: (err) => {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            toast.error(message ?? t('v2.commissioning.director_approval.failed'));
        },
    });

    const submit = async () => {
        if (!ready) return;
        await onConfirmed(code, reason.trim());
        setCode('');
        setReason('');
        setSent(false);
        setApproverId(null);
        onClose();
    };

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            title={title ?? t('v2.commissioning.history_month.wallet_title')}
            description={t('v2.commissioning.director_approval.hint')}
            size="md"
            footer={(
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={onClose}>
                        {t('v2.commissioning.director_approval.close')}
                    </Button>
                    <Button type="button" onClick={() => void submit()} disabled={!ready}>
                        {t('v2.commissioning.director_approval.confirm')}
                    </Button>
                </div>
            )}
        >
            <div className="space-y-3">
                {approvers.isSuccess && approverList.length === 0 ? (
                    <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                        {t('v2.commissioning.director_approval.approver_none')}
                    </p>
                ) : (
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.commissioning.director_approval.approver_label')}
                        <select
                            value={approverId ?? ''}
                            onChange={(e) => {
                                setApproverId(e.target.value ? Number(e.target.value) : null);
                                setSent(false);
                            }}
                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        >
                            <option value="">—</option>
                            {approverList.map((u) => (
                                <option key={u.id} value={u.id}>{u.name}{u.email ? ` · ${u.email}` : ''}</option>
                            ))}
                        </select>
                    </label>
                )}
                <Button
                    type="button"
                    variant="outline"
                    isLoading={requestCode.isPending}
                    disabled={approverId == null}
                    onClick={() => requestCode.mutate()}
                >
                    {sent ? t('v2.commissioning.director_approval.resend') : t('v2.commissioning.director_approval.send')}
                </Button>
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.commissioning.history_month.wallet_code')}
                    <input
                        inputMode="numeric"
                        maxLength={6}
                        value={code}
                        onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-lg tracking-[0.4em] dark:border-zinc-700 dark:bg-zinc-900"
                    />
                </label>
                <label className="block text-xs font-semibold text-gray-600">
                    {t('v2.commissioning.director_approval.reason')}
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                    />
                </label>
            </div>
        </V2Modal>
    );
}

export default function CommissionWalletPanel({
    sellerUserId,
    wallet,
    applied,
    afterWallet,
    month,
}: {
    sellerUserId?: number | null;
    wallet?: WalletSnapshot | null;
    applied?: number;
    afterWallet?: number;
    month?: string;
}) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const { canWallet } = useCommissionActionPermissions();
    const [mode, setMode] = useState<'allocate' | 'forgive' | 'manual' | null>(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [amounts, setAmounts] = useState<Record<number, string>>({});
    const [manualAmount, setManualAmount] = useState('');
    const [forgiveAmount, setForgiveAmount] = useState('');

    const deals = wallet?.allocatable_deals ?? [];
    const balance = wallet?.balance ?? 0;
    const selected = useMemo(
        () => deals
            .map((d) => ({ id: d.ploomes_deal_id, amount: Number(String(amounts[d.ploomes_deal_id] ?? '').replace(',', '.')) }))
            .filter((row) => row.amount > 0),
        [amounts, deals],
    );

    if (!sellerUserId || !wallet) return null;
    if (balance <= 0.009 && (applied ?? 0) <= 0.009 && (wallet.entries?.length ?? 0) === 0) return null;

    const invalidate = () => {
        void qc.invalidateQueries({ queryKey: ['commissioning', 'seller-month-history'] });
    };

    const run = async (code: string, reason: string) => {
        if (!mode) return;
        try {
            if (mode === 'allocate') {
                await api.post(`/commissioning/sellers/${sellerUserId}/wallet/allocate`, {
                    approval_code: code,
                    allocations: selected.map((row) => ({
                        ploomes_deal_id: row.id,
                        amount: row.amount,
                        competence_month: month,
                    })),
                });
            } else if (mode === 'forgive') {
                await api.post(`/commissioning/sellers/${sellerUserId}/wallet/forgive`, {
                    approval_code: code,
                    amount: Number(forgiveAmount.replace(',', '.')),
                    note: reason,
                });
            } else {
                await api.post(`/commissioning/sellers/${sellerUserId}/wallet/clawback`, {
                    approval_code: code,
                    amount: Number(manualAmount.replace(',', '.')),
                    note: reason,
                });
            }
            toast.success(t('v2.commissioning.wallet.done'));
            setConfirmOpen(false);
            setMode(null);
            invalidate();
        } catch {
            toast.error(t('v2.commissioning.director_approval.invalid'));
        }
    };

    return (
        <section className="mb-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-700 dark:bg-amber-950/30">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                    <span className="flex size-10 items-center justify-center rounded-full bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100">
                        <FaWallet className="size-4" aria-hidden />
                    </span>
                    <div>
                        <p className="text-sm font-bold text-amber-950 dark:text-amber-100">
                            {t('v2.commissioning.history_month.wallet_title')}
                        </p>
                        <p className="mt-1 text-xs text-amber-800 dark:text-amber-200">
                            {t('v2.commissioning.history_month.wallet_balance')}: {brl(balance)}
                            {' · '}
                            {t('v2.commissioning.history_month.wallet_applied')}: {brl(applied ?? 0)}
                            {' · '}
                            {t('v2.commissioning.history_month.wallet_after')}: {brl(afterWallet ?? 0)}
                        </p>
                    </div>
                </div>
                {canWallet && balance > 0.009 && (
                    <div className="flex flex-wrap gap-2">
                        <Button type="button" size="sm" variant="outline" onClick={() => setMode('allocate')}>
                            {t('v2.commissioning.history_month.wallet_allocate')}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => { setForgiveAmount(String(balance)); setMode('forgive'); setConfirmOpen(true); }}>
                            {t('v2.commissioning.history_month.wallet_forgive')}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => { setMode('manual'); setConfirmOpen(true); }}>
                            {t('v2.commissioning.wallet.manual')}
                        </Button>
                    </div>
                )}
            </div>

            {mode === 'allocate' && (
                <div className="mt-3 space-y-2">
                    <p className="text-xs text-amber-900 dark:text-amber-100">{t('v2.commissioning.wallet.pick_deals')}</p>
                    <div className="max-h-48 space-y-1 overflow-auto">
                        {deals.map((deal) => (
                            <label key={deal.ploomes_deal_id} className="flex items-center justify-between gap-2 rounded-lg bg-white/70 px-2 py-1 text-xs dark:bg-zinc-900/40">
                                <span className="truncate">{deal.sale_number || deal.ploomes_deal_id} · {deal.title || '—'}</span>
                                <input
                                    inputMode="decimal"
                                    value={amounts[deal.ploomes_deal_id] ?? ''}
                                    onChange={(e) => setAmounts((prev) => ({ ...prev, [deal.ploomes_deal_id]: e.target.value }))}
                                    className="w-28 rounded border border-amber-200 px-2 py-1 text-right dark:border-amber-800 dark:bg-zinc-900"
                                    placeholder="0,00"
                                />
                            </label>
                        ))}
                    </div>
                    <Button type="button" size="sm" disabled={selected.length === 0} onClick={() => setConfirmOpen(true)}>
                        {t('v2.commissioning.history_month.wallet_code')}
                    </Button>
                </div>
            )}

            <CommissionApprovalModal
                sellerUserId={sellerUserId}
                open={confirmOpen && mode != null}
                action={mode === 'allocate' ? 'wallet_allocate' : mode === 'forgive' ? 'wallet_forgive' : 'wallet_manual'}
                title={t('v2.commissioning.history_month.wallet_title')}
                onClose={() => { setConfirmOpen(false); if (mode !== 'allocate') setMode(null); }}
                onConfirmed={run}
            />
        </section>
    );
}

export function WalletAppliedSection({
    sellerUserId,
    month,
    items,
    excess,
    closed,
}: {
    sellerUserId?: number | null;
    month: string;
    items?: WalletAppliedItem[];
    excess?: number;
    closed?: boolean;
}) {
    const { t } = useI18n();
    const qc = useQueryClient();
    const { canMoveWallet } = useCommissionActionPermissions();
    const [moveOpen, setMoveOpen] = useState(false);
    const [target, setTarget] = useState('');
    const [amount, setAmount] = useState('');
    const [reallocating, setReallocating] = useState<WalletAppliedItem | null>(null);
    const [infoFor, setInfoFor] = useState<number | null>(null);
    const canReallocate = isSuperAdmin() && !closed && !!sellerUserId;
    const monthLabel = (ym?: string | null) => {
        if (!ym) return '—';
        const [y, m] = ym.split('-').map(Number);
        return new Date(y, (m ?? 1) - 1, 1).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' });
    };

    const move = useMutation({
        mutationFn: () => api.post(`/commissioning/sellers/${sellerUserId}/wallet/move`, {
            from_month: month,
            targets: [{ competence_month: target, amount: Number(amount.replace(',', '.')) }],
        }),
        onSuccess: (res) => {
            toast.success(res.data?.message ?? t('v2.commissioning.wallet.moved'));
            setMoveOpen(false);
            void qc.invalidateQueries({ queryKey: ['commissioning', 'seller-month-history'] });
        },
        onError: (err) => {
            const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
            toast.error(message ?? t('v2.commissioning.wallet.month_closed'));
        },
    });

    const rows = (items ?? []).filter((row) => Math.abs(row.amount) > 0.009 || (row.moved_out?.length ?? 0) > 0);
    if (rows.length === 0) return null;

    const total = rows.reduce((acc, row) => acc + row.amount, 0);
    const kindLabel = (kind: WalletAppliedItem['kind']) => t(`v2.commissioning.wallet.clawback_kind_${kind}`);

    return (
        <section className="mb-3 rounded-2xl border border-rose-200 bg-rose-50/60 p-4 dark:border-rose-900/50 dark:bg-rose-950/20">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-bold text-rose-900 dark:text-rose-100">{t('v2.commissioning.wallet.clawback_section')}</p>
                <p className="text-sm font-black tabular-nums text-rose-700 dark:text-rose-300">− {brl(total)}</p>
            </div>
            <ul className="mt-2 divide-y divide-rose-100 dark:divide-rose-900/40">
                {rows.map((row) => (
                    <li key={row.id} className="py-1.5 text-xs">
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <p className="truncate font-semibold text-gray-900 dark:text-zinc-100">
                                    {row.deal_number ? `#${row.deal_number} · ` : ''}{row.deal_title ?? row.note ?? '—'}
                                </p>
                                <p className="text-[11px] text-rose-700 dark:text-rose-300">
                                    {kindLabel(row.kind)}{row.commission_role ? ` · ${row.commission_role}` : ''}
                                    {row.kind === 'reallocated' && row.origin_month && (
                                        <span> · {t('v2.commissioning.wallet.reallocate_from', { month: monthLabel(row.origin_month) })}</span>
                                    )}
                                </p>
                                {(row.moved_out?.length ?? 0) > 0 && (
                                    <p className="mt-0.5 text-[10px] text-gray-500 dark:text-zinc-400">
                                        {brl(row.original_amount ?? 0)} →{' '}
                                        {row.moved_out!.map((o, i) => (
                                            <span key={i}>
                                                {i > 0 ? ' · ' : ''}{t('v2.commissioning.wallet.reallocate_moved_to', { month: monthLabel(o.competence_month) })} {brl(o.amount)}
                                            </span>
                                        ))}
                                    </p>
                                )}
                                {row.reason && <p className="mt-0.5 text-[10px] italic text-gray-500">{row.reason}</p>}
                            </div>
                            <div className="flex shrink-0 items-center gap-1.5">
                                <span className="font-semibold tabular-nums text-rose-700 dark:text-rose-300">
                                    {row.amount >= 0 ? '− ' : '+ '}{brl(Math.abs(row.amount))}
                                </span>
                                {canReallocate && row.amount > 0.009 && (
                                    <>
                                        <Button type="button" size="sm" variant="outline" onClick={() => setReallocating(row)}>
                                            <PiArrowsSplitBold className="mr-1 size-3.5" aria-hidden />
                                            {t('v2.commissioning.wallet.reallocate_button')}
                                        </Button>
                                        <button
                                            type="button"
                                            onClick={() => setInfoFor(infoFor === row.id ? null : row.id)}
                                            className="rounded-full p-1 text-rose-500 hover:bg-rose-100 dark:hover:bg-rose-900/40"
                                            aria-label={t('v2.commissioning.wallet.reallocate_info_title')}
                                            aria-expanded={infoFor === row.id}
                                        >
                                            <PiInfoBold className="size-4" />
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                        {infoFor === row.id && (
                            <div className="mt-1.5 rounded-lg border border-rose-200 bg-white p-2.5 text-[11px] leading-relaxed text-gray-700 dark:border-rose-900/50 dark:bg-zinc-900 dark:text-zinc-300">
                                <p className="mb-0.5 font-bold text-gray-900 dark:text-zinc-100">{t('v2.commissioning.wallet.reallocate_info_title')}</p>
                                {t('v2.commissioning.wallet.reallocate_info')}
                            </div>
                        )}
                    </li>
                ))}
            </ul>

            {(excess ?? 0) > 0.009 && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-amber-100 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                    <span>{t('v2.commissioning.wallet.excess_warning', { amount: brl(excess ?? 0) })}</span>
                    {canMoveWallet && !closed && sellerUserId && (
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setAmount(String(excess ?? 0));
                                setTarget('');
                                setMoveOpen(true);
                            }}
                        >
                            {t('v2.commissioning.wallet.move_button')}
                        </Button>
                    )}
                </div>
            )}

            {sellerUserId && (
                <WalletReallocateModal sellerUserId={sellerUserId} month={month} item={reallocating} onClose={() => setReallocating(null)} />
            )}

            <V2Modal
                open={moveOpen}
                onClose={() => setMoveOpen(false)}
                title={t('v2.commissioning.wallet.move_button')}
                size="md"
                footer={(
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setMoveOpen(false)}>
                            {t('v2.commissioning.director_approval.close')}
                        </Button>
                        <Button
                            type="button"
                            isLoading={move.isPending}
                            disabled={!target || !(Number(amount.replace(',', '.')) > 0)}
                            onClick={() => move.mutate()}
                        >
                            {t('v2.commissioning.wallet.move_confirm')}
                        </Button>
                    </div>
                )}
            >
                <div className="space-y-3">
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.commissioning.wallet.move_target')}
                        <input
                            type="month"
                            value={target}
                            min={month}
                            onChange={(e) => setTarget(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                    </label>
                    <label className="block text-xs font-semibold text-gray-600">
                        {t('v2.commissioning.wallet.move_amount')}
                        <input
                            inputMode="decimal"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-right text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                    </label>
                </div>
            </V2Modal>
        </section>
    );
}
